<template>
    <div class="debug-music-note-container">
        <h1 class="text-h3 text-primary">🎵 Music Note Component Demo</h1>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Note Properties</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Note Selection</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="note-input">Note:</label>
                            <input
                                id="note-input"
                                v-model="noteValue"
                                type="text"
                                placeholder="e.g., C4, D#5, Bb4"
                                class="text-input"
                            />
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Clef Selection</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label>Clef:</label>
                            <div class="button-group">
                                <button
                                    @click="setClef('')"
                                    :class="{ active: clefValue === '' }"
                                    class="state-btn none"
                                >
                                    None
                                </button>
                                <button
                                    @click="setClef('treble')"
                                    :class="{ active: clefValue === 'treble' }"
                                    class="state-btn treble"
                                >
                                    Treble
                                </button>
                                <button
                                    @click="setClef('bass')"
                                    :class="{ active: clefValue === 'bass' }"
                                    class="state-btn bass"
                                >
                                    Bass
                                </button>
                                <button
                                    @click="setClef('alto')"
                                    :class="{ active: clefValue === 'alto' }"
                                    class="state-btn alto"
                                >
                                    Alto
                                </button>
                                <button
                                    @click="setClef('tenor')"
                                    :class="{ active: clefValue === 'tenor' }"
                                    class="state-btn tenor"
                                >
                                    Tenor
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Duration Selection</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label>Duration:</label>
                            <div class="button-group duration-buttons">
                                <button
                                    @click="setDuration('')"
                                    :class="{ active: durationValue === '' }"
                                    class="state-btn none"
                                >
                                    None
                                </button>
                                <button
                                    @click="setDuration('whole')"
                                    :class="{
                                        active: durationValue === 'whole',
                                    }"
                                    class="state-btn duration"
                                >
                                    Whole
                                </button>
                                <button
                                    @click="setDuration('half')"
                                    :class="{
                                        active: durationValue === 'half',
                                    }"
                                    class="state-btn duration"
                                >
                                    Half
                                </button>
                                <button
                                    @click="setDuration('quarter')"
                                    :class="{
                                        active: durationValue === 'quarter',
                                    }"
                                    class="state-btn duration"
                                >
                                    Quarter
                                </button>
                                <button
                                    @click="setDuration('eighth')"
                                    :class="{
                                        active: durationValue === 'eighth',
                                    }"
                                    class="state-btn duration"
                                >
                                    Eighth
                                </button>
                                <button
                                    @click="setDuration('sixteenth')"
                                    :class="{
                                        active: durationValue === 'sixteenth',
                                    }"
                                    class="state-btn duration"
                                >
                                    Sixteenth
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Custom Duration</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="custom-duration"
                                >Custom Duration:</label
                            >
                            <input
                                id="custom-duration"
                                v-model="customDuration"
                                type="text"
                                placeholder="e.g., dotted quarter, triplet"
                                class="text-input"
                            />
                            <button
                                @click="setDuration(customDuration)"
                                class="apply-btn"
                            >
                                Apply Custom
                            </button>
                        </div>
                    </div>
                </div>

                <div class="actions">
                    <button @click="clearAll" class="action-btn clear">
                        Clear All
                    </button>
                    <button @click="setExample" class="action-btn fill">
                        Set Example
                    </button>
                </div>
            </div>

            <!-- Right Panel: Preview -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Preview</h2>
                <div class="preview-box">
                    <MusicNote
                        :note="activeNote"
                        :clef="activeClef"
                        :duration="activeDuration"
                        v-bind="widthHeightProps"
                    />
                </div>

                <div class="size-controls bg-surface-light">
                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="width-slider"
                                >Width: {{ previewWidth }}px</label
                            >
                            <div class="checkbox-wrapper">
                                <input
                                    id="enable-width"
                                    type="checkbox"
                                    v-model="enableWidth"
                                />
                                <label for="enable-width"
                                    >Pass width prop</label
                                >
                            </div>
                        </div>
                        <input
                            id="width-slider"
                            type="range"
                            v-model.number="previewWidth"
                            min="150"
                            max="400"
                            step="25"
                            :disabled="!enableWidth"
                        />
                    </div>

                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="height-slider"
                                >Height: {{ previewHeight }}px</label
                            >
                            <div class="checkbox-wrapper">
                                <input
                                    id="enable-height"
                                    type="checkbox"
                                    v-model="enableHeight"
                                />
                                <label for="enable-height"
                                    >Pass height prop</label
                                >
                            </div>
                        </div>
                        <input
                            id="height-slider"
                            type="range"
                            v-model.number="previewHeight"
                            min="80"
                            max="200"
                            step="20"
                            :disabled="!enableHeight"
                        />
                    </div>
                </div>

                <div class="preview-info bg-surface-light">
                    <h3 class="text-h6">Current Props:</h3>
                    <div class="prop-display">
                        <div>
                            <strong>note:</strong> "{{
                                activeNote || "(empty)"
                            }}"
                        </div>
                        <div>
                            <strong>clef:</strong> "{{
                                activeClef || "(empty)"
                            }}"
                        </div>
                        <div>
                            <strong>duration:</strong> "{{
                                activeDuration || "(empty)"
                            }}"
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Examples Section -->
        <div class="examples-section">
            <h2 class="text-h5 text-secondary">Common Note Examples</h2>
            <div class="examples-grid">
                <div
                    v-for="example in examples"
                    :key="example.name"
                    class="example-card"
                >
                    <h3>{{ example.name }}</h3>
                    <MusicNote
                        :note="example.note"
                        :clef="example.clef"
                        :duration="example.duration"
                        v-bind="exampleWidthHeightProps"
                    />
                    <button @click="loadExample(example)" class="load-btn">
                        Load
                    </button>
                </div>
            </div>
        </div>

        <!-- Code Output -->
        <div class="code-section">
            <h2>Component Code</h2>
            <pre><code>{{ componentCode }}</code></pre>
            <button @click="copyCode" class="copy-btn">📋 Copy</button>
        </div>

        <!-- API Documentation -->
        <v-card class="api-section">
            <v-card-title class="text-h5 text-secondary">
                API Documentation
            </v-card-title>
            <v-card-text>
                <h3 class="text-h6 mb-3">Props</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Prop</th>
                            <th>Type</th>
                            <th>Default</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>note</code></td>
                            <td><code>string</code></td>
                            <td>"C4"</td>
                            <td>Note to display (e.g., "C4", "F#5", "Bb3")</td>
                        </tr>
                        <tr>
                            <td><code>clef</code></td>
                            <td><code>string</code></td>
                            <td>"treble"</td>
                            <td>Musical clef (treble, bass, alto, tenor)</td>
                        </tr>
                        <tr>
                            <td><code>duration</code></td>
                            <td><code>string</code></td>
                            <td>"quarter"</td>
                            <td>Note duration (whole, half, quarter, eighth, sixteenth)</td>
                        </tr>
                        <tr>
                            <td><code>width</code></td>
                            <td><code>number</code></td>
                            <td>200</td>
                            <td>Width of the display in pixels</td>
                        </tr>
                        <tr>
                            <td><code>height</code></td>
                            <td><code>number</code></td>
                            <td>120</td>
                            <td>Height of the display in pixels</td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">Features</h3>
                <ul class="api-list">
                    <li><strong>VexFlow rendering:</strong> Professional music notation using VexFlow library</li>
                    <li><strong>Multiple clefs:</strong> Supports treble, bass, alto, and tenor clefs</li>
                    <li><strong>All durations:</strong> Whole, half, quarter, eighth, sixteenth, and more</li>
                    <li><strong>Accidentals:</strong> Handles sharps (#) and flats (b/Bb)</li>
                    <li><strong>Centered display:</strong> Note is automatically centered on the staff</li>
                    <li><strong>Responsive:</strong> SVG-based rendering scales cleanly</li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Note Format</h3>
                <ul class="api-list">
                    <li><strong>Natural notes:</strong> "C4", "D5", "E6"</li>
                    <li><strong>Sharp notes:</strong> "C#4", "F#5"</li>
                    <li><strong>Flat notes:</strong> "Bb4", "Eb5" (both "b" and "Bb" work)</li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Usage Example</h3>
                <pre class="usage-example"><code>&lt;MusicNote
  note="C5"
  clef="treble"
  duration="quarter"
  :width="200"
  :height="120"
/&gt;</code></pre>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import MusicNote from "../components/MusicNote.vue";

interface Example {
    name: string;
    note?: string;
    clef?: string;
    duration?: string;
}

const noteValue = ref("C4");
const clefValue = ref("treble");
const durationValue = ref("quarter");
const customDuration = ref("");
const previewWidth = ref(250);
const previewHeight = ref(120);
const enableWidth = ref(true);
const enableHeight = ref(true);

const activeNote = computed(() => noteValue.value || undefined);
const activeClef = computed(() => clefValue.value || undefined);
const activeDuration = computed(() => durationValue.value || undefined);

const widthHeightProps = computed(() => {
    const props: Record<string, number> = {};
    if (enableWidth.value) {
        props.width = previewWidth.value;
    }
    if (enableHeight.value) {
        props.height = previewHeight.value;
    }
    return props;
});

const exampleWidthHeightProps = computed(() => {
    const props: Record<string, number> = {};
    if (enableWidth.value) {
        props.width = 180;
    }
    if (enableHeight.value) {
        props.height = 100;
    }
    return props;
});

const componentCode = computed(() => {
    const props: string[] = [];

    if (activeNote.value) {
        props.push(`  note="${activeNote.value}"`);
    }

    if (activeClef.value) {
        props.push(`  clef="${activeClef.value}"`);
    }

    if (activeDuration.value) {
        props.push(`  duration="${activeDuration.value}"`);
    }

    if (enableWidth.value) {
        props.push(`  :width="${previewWidth.value}"`);
    }
    if (enableHeight.value) {
        props.push(`  :height="${previewHeight.value}"`);
    }

    const propsString = props.length > 0 ? "\n" + props.join("\n") + "\n" : "";

    return `<MusicNote${propsString}/>`;
});

function setClef(clef: string) {
    clefValue.value = clef;
}

function setDuration(duration: string) {
    durationValue.value = duration;
}

function clearAll() {
    noteValue.value = "";
    clefValue.value = "";
    durationValue.value = "";
    customDuration.value = "";
}

function setExample() {
    noteValue.value = "C4";
    clefValue.value = "treble";
    durationValue.value = "quarter";
}

const examples: Example[] = [
    {
        name: "Middle C",
        note: "C4",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "G Note",
        note: "G4",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "Half Note",
        note: "F4",
        clef: "treble",
        duration: "half",
    },
    {
        name: "Whole Note",
        note: "E4",
        clef: "treble",
        duration: "whole",
    },
    {
        name: "Eighth Note",
        note: "A4",
        clef: "treble",
        duration: "eighth",
    },
    {
        name: "F Sharp",
        note: "F#4",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "B Flat",
        note: "Bb4",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "Bass Clef",
        note: "C3",
        clef: "bass",
        duration: "quarter",
    },
    {
        name: "Alto Clef",
        note: "C4",
        clef: "alto",
        duration: "quarter",
    },
    {
        name: "High Note",
        note: "C6",
        clef: "treble",
        duration: "quarter",
    },
];

function loadExample(example: Example) {
    noteValue.value = example.note || "";
    clefValue.value = example.clef || "";
    durationValue.value = example.duration || "";
}

async function copyCode() {
    try {
        await navigator.clipboard.writeText(componentCode.value);
        alert("Code copied to clipboard!");
    } catch (err) {
        console.error("Failed to copy:", err);
    }
}
</script>

<style scoped>
.debug-music-note-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 30px;
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.text-input {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 8px;
}

.text-input:focus {
    outline: none;
}

.apply-btn {
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: background 0.2s;
}

.apply-btn:hover {
    opacity: 0.9;
}

.button-group {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.duration-buttons {
    flex-direction: column;
    gap: 5px;
}

.duration-buttons .state-btn {
    justify-self: stretch;
}

.state-btn {
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    flex: 1;
    text-align: center;
    font-weight: 500;
}

.state-btn:hover {
    opacity: 0.8;
}

.state-btn.active {
    font-weight: bold;
}

.actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
    padding-top: 20px;
}

.action-btn {
    flex: 1;
    padding: 10px;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.action-btn:hover {
    opacity: 0.9;
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    border-radius: 6px;
    min-height: 150px;
}

.size-controls {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
}

.size-controls label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 14px;
}

.size-control-group {
    margin-bottom: 20px;
}

.size-control-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.size-control-header label {
    margin-bottom: 0;
}

.checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 5px;
}

.checkbox-wrapper input[type="checkbox"] {
    margin: 0;
}

.checkbox-wrapper label {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 0;
    opacity: 0.7;
}

.size-controls input[type="range"] {
    width: 100%;
    margin-bottom: 0;
}

.size-controls input[type="range"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.preview-info {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
}

.prop-display {
    margin-top: 10px;
}

.prop-display div {
    margin-bottom: 5px;
    font-family: monospace;
    font-size: 14px;
    padding: 8px;
    border-radius: 3px;
    font-weight: 500;
}

.examples-section {
    margin: 40px 0;
}

.examples-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 15px;
    margin-top: 15px;
}

.example-card {
    border-radius: 8px;
    padding: 15px;
    text-align: center;
    transition: all 0.2s;
}

.example-card:hover {
    transform: translateY(-2px);
}

.example-card h3 {
    margin: 0 0 10px 0;
    font-size: 16px;
}

.load-btn {
    margin-top: 10px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}

.load-btn:hover {
    opacity: 0.9;
}

.code-section {
    position: relative;
    padding: 20px;
    border-radius: 8px;
    margin-top: 40px;
}

.code-section h2 {
    margin-top: 0;
}

.code-section pre {
    margin: 0;
    overflow-x: auto;
}

.code-section code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

.copy-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}

.copy-btn:hover {
    opacity: 0.9;
}

.api-section {
    margin-top: 40px;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

.usage-example {
    background: rgba(0, 0, 0, 0.05);
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 8px 0;
}

.usage-example code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
